import { RedirectFunction, Routes } from '@angular/router';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { AboutUsComponent } from './pages/about-us/about-us.component';
import { ContactComponent } from './pages/contact/contact.component';
import { BlogsComponent } from './pages/blogs/blogs.component';
import { SingleBlogComponent } from './pages/single-blog/single-blog.component';
import { JoinAsProfessionalComponent } from './pages/join-as-professional/join-as-professional.component';
import { AuctionsCalendarComponent } from './pages/auctions-calendar/auctions-calendar.component';
import { TermsConditionsViewComponent } from 'src/app/Shared/pages/terms-conditions-view/terms-conditions-view.component';
import { blogSeoResolver } from './_resolvers/blogs-seo.resolver';
import { seoResolverResolver } from 'src/app/Core/_resolvers/seo-resolver.resolver';
import { InternationalAuctionsIndexComponent } from './pages/international-auctions/index/international-auctions-index.component';
import { InternationalAuctionsHouseComponent } from './pages/international-auctions/house/international-auctions-house.component';
import { InternationalAuctionsSaleComponent } from './pages/international-auctions/sale/international-auctions-sale.component';
import { InternationalAuctionsLotComponent } from './pages/international-auctions/lot/international-auctions-lot.component';

/**
 * BXS-3627: the two `external-catalogue/*` concept URLs redirect here, keeping
 * their `:provider` value. A plain `redirectTo` string cannot carry a param
 * across to a differently-shaped path, so this is a `RedirectFunction` — it
 * reads `params` from the matched route and returns a RELATIVE path, which the
 * router resolves against this same (public-layout) config. Relative matters:
 * the `:locale` segment is consumed by the parent route in `app.routes.ts` and
 * must survive the redirect, so `/en/external-catalogue/arqana` lands on
 * `/en/international-auctions/arqana` and never on `/international-auctions/…`.
 */
const redirectConceptUrlToInternationalAuctions: RedirectFunction = ({
  params,
}) => {
  const provider = params['provider'] as string | undefined;
  return provider
    ? `international-auctions/${provider}`
    : 'international-auctions';
};

export const routes: Routes = [
  // International Auctions — partner auction house catalogues (BXS-3627).
  // Paths carry NO locale segment: the parent `:locale` route supplies it.
  // Most specific first, so `/:house/:sale/:lot` is not swallowed by `/:house`.
  {
    path: 'international-auctions/:house/:sale/:lot',
    component: InternationalAuctionsLotComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Lot Details | BidxSell',
      description:
        'Pedigree, photographs, documents and catalogue updates for this lot, as published by the partner auction house.',
    },
  },
  {
    path: 'international-auctions/:house/:sale',
    component: InternationalAuctionsSaleComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Sale Catalogue | BidxSell',
      description:
        'Browse the full lot catalogue for this partner sale on BidxSell. Preview only — registration and bidding take place with the auction house.',
    },
  },
  {
    path: 'international-auctions/:house',
    component: InternationalAuctionsHouseComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Partner Auction House | BidxSell',
      description:
        'Every sale this partner auction house has listed on BidxSell, with catalogues, dates and venues.',
    },
  },
  {
    path: 'international-auctions',
    component: InternationalAuctionsIndexComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'International Auctions - Partner Sales | BidxSell',
      description:
        'Browse catalogues from Tattersalls, Arqana and Fasig-Tipton on BidxSell. Preview lots, pedigrees and results; register and bid with the auction house.',
    },
  },

  // BXS-3627: superseded concept URLs. `external-catalogue/*` was the dummy,
  // static-data prototype (ExternalCatalogueComponent / ExternalCatalogueList-
  // Component — both still on disk, now unrouted, kept for reference until the
  // real UI lands). Those links are already circulating on dev, so they must
  // keep working; `international-auctions` is the public name settings.md fixes.
  // This is a client-side redirect only. When this ships, the permanent 301
  // belongs in `server.ts` so crawlers and non-Angular clients get it too.
  // Order matters: `/:provider/horse` sits above `/:provider`, exactly as the
  // two component routes did, so the longer path is not consumed by the shorter.
  {
    path: 'external-catalogue/:provider/horse',
    redirectTo: redirectConceptUrlToInternationalAuctions,
  },
  {
    path: 'external-catalogue/:provider',
    redirectTo: redirectConceptUrlToInternationalAuctions,
  },
  {
    path: '',
    component: LandingPageComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'BidxSell - Premier Horse Auction Platform',
      description:
        'Discover premium horses at BidxSell, the leading online horse auction platform. Bid on thoroughbreds, racing horses, and breeding stock from top sellers worldwide.',
    },
  },
  {
    path: 'auctions-calendar',
    component: AuctionsCalendarComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Auctions Calendar - BidxSell Horse Auction Platform',
      description:
        'View the complete calendar of upcoming horse auctions at BidxSell. Plan ahead and never miss an auction with our comprehensive yearly calendar.',
    },
  },
  {
    path: 'about-us',
    component: AboutUsComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'About Us - BidxSell Horse Auction Platform',
      description:
        "Learn about BidxSell's mission to revolutionize horse auctions. Discover our story, values, and commitment to connecting horse enthusiasts worldwide.",
    },
  },
  {
    path: 'contact',
    component: ContactComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Contact Us - BidxSell Support & Information',
      description:
        'Get in touch with BidxSell for auction support, seller inquiries, or technical assistance. Our expert team is here to help with all your horse auction needs.',
    },
  },
  {
    path: 'join-as-professional',
    component: JoinAsProfessionalComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Join as Professional Seller - BidxSell',
      description:
        'Become a professional seller on BidxSell. Reach thousands of qualified buyers, maximize your horse sales, and grow your equestrian business.',
    },
  },
  {
    path: 'blogs',
    component: BlogsComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Horse Auction Blog - Tips, News & Insights | BidxSell',
      description:
        'Stay updated with the latest horse auction trends, bidding tips, and equestrian news. Expert insights for buyers and sellers in the horse industry.',
    },
  },
  {
    path: 'blog-details/:slug',
    component: SingleBlogComponent,
    resolve: { seo: blogSeoResolver },
  },
  {
    path: 'terms-and-conditions/:type/:auctionId',
    component: TermsConditionsViewComponent,
    resolve: { seo: seoResolverResolver },
    data: {
      title: 'Terms and Conditions - BidxSell',
      description:
        'Read BidxSell terms and conditions for horse auctions. Understand bidding rules, payment terms, and auction policies.',
    },
  },
];
